import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WifiIcon from '@mui/icons-material/Wifi';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import HomeIcon from '@mui/icons-material/Home';
import PanToolAltIcon from '@mui/icons-material/PanToolAlt';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Sample High-Quality Event Images & Details for User Example
const SAMPLE_EVENTS = [
  {
    id: 1,
    name: 'Youth Event',
    month: 'Nov',
    date: '12',
    fullDate: 'Thu, Nov 12, 11:00 AM GMT+5:30',
    description: 'Youth event description - Join us for an inspiring evening of worship, fellowship, practical discussions on faith, and community bonding. Refreshments and breakout sessions included!',
    image: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=700&q=80',
    location: 'Main Church Auditorium & Live Stream',
    email: 'test@gmail.com',
    phone: '+919769879760',
    buttonText: 'Our Website',
    url: 'https://example.org/youth',
  },
  {
    id: 2,
    name: 'Christmas Eve Candlelight Service',
    month: 'Dec',
    date: '24',
    fullDate: 'Wed, Dec 24, 06:30 PM GMT+5:30',
    description: 'Celebrate the joy of the Savior\'s birth with beautiful carols, candlelight reflections, musical presentations by our choir, and an encouraging message of hope.',
    image: 'https://images.unsplash.com/photo-1543257580-7269da773bf5?auto=format&fit=crop&w=700&q=80',
    location: 'Sanctuary & Community Center',
    email: 'events@waytruthlife.org',
    phone: '+919840112233',
    buttonText: 'Reserve Free Seat',
    url: 'https://example.org/christmas',
  },
  {
    id: 3,
    name: 'Kingdom Leadership Summit 2026',
    month: 'Jan',
    date: '15',
    fullDate: 'Thu, Jan 15, 09:00 AM GMT+5:30',
    description: 'Equipping ministry coordinators, small-group facilitators, and young leaders with strategic vision, biblical principles, and collaborative workshops.',
    image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&w=700&q=80',
    location: 'Grace Fellowship Hall, Room 302',
    email: 'leadership@waytruthlife.org',
    phone: '+919700223344',
    buttonText: 'Download Schedule',
    url: 'https://example.org/summit',
  },
];

export default function MobileEventPreviewModal({
  open = false,
  onClose,
  isExample = true,
  liveEvent = null,
  churchName = 'Way Truth Life Church',
}) {
  // Screen mode: 'list' (Events Listing) | 'details' (Event Specific View)
  const [screenMode, setScreenMode] = useState('list');
  const [selectedEventId, setSelectedEventId] = useState(1);

  // Active event data (either live from Add Event form or sample)
  const activeEvent = liveEvent && liveEvent.eventName ? {
    id: 999,
    name: liveEvent.eventName || 'Annual Youth Conference',
    month: liveEvent.startDate ? new Date(liveEvent.startDate).toLocaleString('en-US', { month: 'short' }) : 'Nov',
    date: liveEvent.startDate ? String(new Date(liveEvent.startDate).getDate()).padStart(2, '0') : '12',
    fullDate: liveEvent.startDate
      ? new Date(liveEvent.startDate).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })
      : 'Thu, Nov 12, 11:00 AM GMT+5:30',
    description: liveEvent.description || 'Join us for an inspiring session of worship and fellowship.',
    image: liveEvent.image?.preview || SAMPLE_EVENTS[0].image,
    location: liveEvent.contactInfo || 'Main Auditorium',
    email: liveEvent.contactEmail || 'test@gmail.com',
    phone: liveEvent.contactNumber || '+919769879760',
    buttonText: liveEvent.url ? 'Our Website' : 'Learn More',
    url: liveEvent.url || 'https://example.org',
  } : (SAMPLE_EVENTS.find(e => e.id === selectedEventId) || SAMPLE_EVENTS[0]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '44px',
          bgcolor: '#070B11',
          p: 0,
          boxShadow: '0 25px 65px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.08)',
          border: '8px solid #1E293B',
          overflow: 'hidden',
          width: '375px',
          maxWidth: '95vw',
          height: '750px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          m: 'auto',
        },
      }}
    >
      {/* ── TOP STATUS BAR (14:00, Notch/Cutout, Wifi, Cellular, Battery 40%) ── */}
      <Box
        sx={{
          bgcolor: '#000000',
          px: 2.2,
          pt: 1.2,
          pb: 0.6,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#FFFFFF',
          fontSize: '0.82rem',
          fontWeight: 700,
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <span>14:00</span>

        {/* Punch Hole Camera Cutout */}
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: '#0F172A',
            border: '2px solid #334155',
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <SignalCellularAltIcon sx={{ fontSize: 15, color: '#FFFFFF' }} />
          <WifiIcon sx={{ fontSize: 15, color: '#FFFFFF' }} />
          <BatteryFullIcon sx={{ fontSize: 17, color: '#FFFFFF' }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, ml: -0.2 }}>40</Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: '#94A3B8',
              p: 0.2,
              ml: 0.8,
              '&:hover': { color: '#EF4444' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── INTERACTIVE SCREEN SWITCHER HEADER BAR ── */}
      <Box
        sx={{
          bgcolor: '#0D1B2A',
          borderBottom: '1.5px solid #1B3A4B',
          px: 1.5,
          py: 0.8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: '#64D2E2', fontSize: '0.75rem', fontWeight: 700 }}>
          {isExample ? '📱 User Example' : '🟢 Live Preview'}
        </Typography>

        <Box display="flex" alignItems="center" gap={0.5}>
          <Button
            size="small"
            onClick={() => setScreenMode('list')}
            sx={{
              bgcolor: screenMode === 'list' ? '#177373' : 'rgba(255,255,255,0.08)',
              color: '#FFFFFF',
              px: 1,
              py: 0.3,
              borderRadius: '12px',
              fontSize: '0.68rem',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: screenMode === 'list' ? '#135c5c' : 'rgba(255,255,255,0.15)' }
            }}
          >
            Events List
          </Button>
          <Button
            size="small"
            onClick={() => setScreenMode('details')}
            sx={{
              bgcolor: screenMode === 'details' ? '#177373' : 'rgba(255,255,255,0.08)',
              color: '#FFFFFF',
              px: 1,
              py: 0.3,
              borderRadius: '12px',
              fontSize: '0.68rem',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: screenMode === 'details' ? '#135c5c' : 'rgba(255,255,255,0.15)' }
            }}
          >
            Event Details →
          </Button>
        </Box>
      </Box>

      {/* ── APP HEADER (Back, Church Name, 3-dots) ── */}
      <Box
        sx={{
          bgcolor: '#000000',
          px: 1.8,
          py: 1.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={0.6}
          onClick={() => {
            if (screenMode === 'details') setScreenMode('list');
          }}
          sx={{ cursor: screenMode === 'details' ? 'pointer' : 'default' }}
        >
          <ArrowBackIosNewIcon sx={{ color: '#FFFFFF', fontSize: 16 }} />
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.92rem', fontWeight: 600 }}>
            Back
          </Typography>
        </Box>

        <Typography
          noWrap
          sx={{
            color: '#FFFFFF',
            fontSize: '1rem',
            fontWeight: 800,
            textAlign: 'center',
            maxWidth: 180,
            lineHeight: 1.2,
          }}
        >
          {churchName}
        </Typography>

        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MoreVertIcon sx={{ color: '#FFFFFF', fontSize: 20 }} />
        </Box>
      </Box>

      {/* ── SCROLLABLE PHONE BODY ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          bgcolor: '#000000',
          color: '#FFFFFF',
          boxSizing: 'border-box',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
        }}
      >
        {/* ======================================================== */}
        {/* SCREEN 1: EVENTS LIST SCREEN                             */}
        {/* ======================================================== */}
        {screenMode === 'list' && (
          <Box sx={{ pb: 3 }}>


            {/* Featured Banner Card (JESUS - Latest Events) */}
            <Box sx={{ p: 2, pb: 1 }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 160,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                  background: 'linear-gradient(180deg, #1A2634 0%, #0F172A 100%)',
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=700&q=80"
                  alt="Latest Events"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'brightness(0.65)',
                  }}
                />

                {/* Overlay Text */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 100%)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '2.4rem',
                      fontWeight: 900,
                      letterSpacing: '3px',
                      color: 'rgba(255,255,255,0.92)',
                      fontFamily: 'Impact, Arial Black, sans-serif',
                      textShadow: '0 4px 12px rgba(0,0,0,0.7)',
                    }}
                  >
                    JESUS
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 800,
                      color: '#FFFFFF',
                      mt: 0.5,
                      textShadow: '0 2px 6px rgba(0,0,0,0.8)',
                    }}
                  >
                    Latest Events
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Events List Items */}
            <Box sx={{ px: 2, pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {SAMPLE_EVENTS.map((event) => (
                <Box
                  key={event.id}
                  onClick={() => {
                    setSelectedEventId(event.id);
                    setScreenMode('details');
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.2,
                    borderRadius: '16px',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.08)',
                      borderColor: '#177373',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {/* Left: Date Display */}
                  <Box
                    sx={{
                      width: 50,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        color: 'rgba(255,255,255,0.85)',
                        fontStyle: 'italic',
                        fontWeight: 600,
                        fontFamily: 'Georgia, serif',
                      }}
                    >
                      {event.month}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.45rem',
                        fontWeight: 800,
                        color: '#FFFFFF',
                        lineHeight: 1.1,
                      }}
                    >
                      {event.date}
                    </Typography>
                  </Box>

                  {/* Center: Event Image Thumbnail (The requested addition!) */}
                  <Box
                    sx={{
                      width: 70,
                      height: 52,
                      borderRadius: '10px',
                      overflow: 'hidden',
                      bgcolor: '#1E293B',
                      flexShrink: 0,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <Box
                      component="img"
                      src={event.image}
                      alt={event.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>

                  {/* Right: Event Title & Date */}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: '0.98rem',
                        fontWeight: 700,
                        color: '#FFFFFF',
                      }}
                    >
                      {event.name}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: '0.75rem',
                        color: '#94A3B8',
                        mt: 0.3,
                      }}
                    >
                      {event.fullDate}
                    </Typography>
                    <Chip
                      size="small"
                      label="Tap for Details"
                      sx={{
                        height: 18,
                        fontSize: '0.62rem',
                        bgcolor: 'rgba(23, 115, 115, 0.25)',
                        color: '#64D2E2',
                        fontWeight: 700,
                        mt: 0.5,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ======================================================== */}
        {/* SCREEN 2: EVENT DETAILS SCREEN                           */}
        {/* ======================================================== */}
        {screenMode === 'details' && (
          <Box sx={{ p: 2.2, pb: 4 }}>
            {/* Event Banner Image (Hero artwork at top) */}
            <Box
              sx={{
                width: '100%',
                height: 180,
                borderRadius: '16px',
                overflow: 'hidden',
                mb: 2.5,
                boxShadow: '0 10px 28px rgba(0,0,0,0.6)',
                bgcolor: '#1E293B',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <Box
                component="img"
                src={activeEvent.image}
                alt={activeEvent.name}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>

            {/* Event Title */}
            <Typography
              sx={{
                fontSize: '1.45rem',
                fontWeight: 800,
                color: '#FFFFFF',
                lineHeight: 1.25,
                mb: 0.6,
              }}
            >
              {activeEvent.name}
            </Typography>

            {/* Event Date & Time */}
            <Typography
              sx={{
                fontSize: '0.88rem',
                color: 'rgba(255,255,255,0.7)',
                mb: 1.5,
              }}
            >
              {activeEvent.fullDate}
            </Typography>

            {/* Location Tag */}
            {activeEvent.location && (
              <Box display="flex" alignItems="center" gap={0.8} sx={{ mb: 2 }}>
                <LocationOnOutlinedIcon sx={{ color: '#64D2E2', fontSize: 18 }} />
                <Typography sx={{ color: '#E2E8F0', fontSize: '0.82rem', fontWeight: 600 }}>
                  {activeEvent.location}
                </Typography>
              </Box>
            )}

            {/* Description */}
            <Typography
              sx={{
                fontSize: '0.88rem',
                color: '#CBD5E1',
                lineHeight: 1.6,
                mb: 3,
                bgcolor: 'rgba(255,255,255,0.03)',
                p: 1.5,
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {activeEvent.description}
            </Typography>

            {/* Big Red Action Button (Our Website) */}
            <Button
              fullWidth
              variant="contained"
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#E50914',
                color: '#FFFFFF',
                borderRadius: '28px',
                py: 1.2,
                fontSize: '0.96rem',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 6px 20px rgba(229, 9, 20, 0.45)',
                mb: 3,
                '&:hover': {
                  bgcolor: '#B80710',
                },
              }}
            >
              {activeEvent.buttonText}
            </Button>

            {/* Contact Section */}
            <Box
              sx={{
                borderTop: '1px solid rgba(255,255,255,0.1)',
                pt: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.98rem',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  mb: 1.5,
                }}
              >
                Contact
              </Typography>

              {/* Email */}
              <Box display="flex" alignItems="center" gap={1.2} mb={1.2}>
                <EmailOutlinedIcon sx={{ color: '#FFFFFF', fontSize: 20 }} />
                <Typography sx={{ color: '#CBD5E1', fontSize: '0.88rem' }}>
                  {activeEvent.email}
                </Typography>
              </Box>

              {/* Phone */}
              <Box display="flex" alignItems="center" gap={1.2}>
                <PhoneInTalkIcon sx={{ color: '#FFFFFF', fontSize: 20 }} />
                <Typography sx={{ color: '#CBD5E1', fontSize: '0.88rem' }}>
                  {activeEvent.phone}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── BOTTOM MOBILE APP TAB BAR (Home, Prayers, Songs, Events, Profile) ── */}
      <Box
        sx={{
          bgcolor: '#000000',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          px: 1,
          py: 0.8,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        {/* Home */}
        <Box display="flex" flexDirection="column" alignItems="center" sx={{ opacity: 0.55 }}>
          <HomeIcon sx={{ color: '#FFFFFF', fontSize: 22 }} />
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.62rem', mt: 0.2 }}>Home</Typography>
        </Box>

        {/* Prayers */}
        <Box display="flex" flexDirection="column" alignItems="center" sx={{ opacity: 0.55 }}>
          <PanToolAltIcon sx={{ color: '#FFFFFF', fontSize: 22 }} />
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.62rem', mt: 0.2 }}>Prayers</Typography>
        </Box>

        {/* Songs */}
        <Box display="flex" flexDirection="column" alignItems="center" sx={{ opacity: 0.55 }}>
          <QueueMusicIcon sx={{ color: '#FFFFFF', fontSize: 22 }} />
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.62rem', mt: 0.2 }}>Songs</Typography>
        </Box>

        {/* Events (Active) */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{
            opacity: 1,
            position: 'relative',
          }}
        >
          <EventAvailableIcon sx={{ color: '#FFFFFF', fontSize: 22 }} />
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.62rem', fontWeight: 800, mt: 0.2 }}>
            Events
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              bottom: -4,
              width: 16,
              height: 2,
              bgcolor: '#64D2E2',
              borderRadius: 1,
            }}
          />
        </Box>

        {/* Profile */}
        <Box display="flex" flexDirection="column" alignItems="center" sx={{ opacity: 0.55 }}>
          <AccountCircleIcon sx={{ color: '#FFFFFF', fontSize: 22 }} />
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.62rem', mt: 0.2 }}>Profile</Typography>
        </Box>
      </Box>

      {/* ── ANDROID HOME NAVIGATION BAR ── */}
      <Box
        sx={{
          bgcolor: '#000000',
          py: 0.5,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 4,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.4)',
          }}
        />
      </Box>
    </Dialog>
  );
}
